/**
 * Board Screen
 *
 * Personal kanban board for the current user.
 * Auto-creates board from role-based template if not exists.
 *
 * This screen is a thin wrapper around KanbanBoard with MyBoard-specific features:
 * - Auto-collapse empty columns to prevent horizontal scroll
 * - Alerts for card move errors
 * - Support for loading user's personal board (userId) or a specific board (boardId)
 * - Board settings are managed by the parent Dashboard component
 */

import { useAuth } from '@/hooks/useAuth';
import { KanbanBoard } from '@/components/core';
import { Icon, IconName } from '@/icons';
import { WeekInfo } from '@/utils/week';
import { BoardPreferences } from '@/types/preferences.types';

interface MyBoardProps {
  boardId?: string;
  onBoardLoad?: (
    boardId: string,
    boardName: string,
    hasEditPermission: boolean,
    isGroupBoard: boolean,
    ownerId: string | undefined,
    templateId: string | undefined
  ) => void;
  initialCardId?: string;
  onInitialCardOpened?: () => void;
  selectedWeek?: WeekInfo;
  /** Board background configuration (passed from Dashboard) */
  boardBackground?: BoardPreferences['background'];
}

export default function MyBoard({
  boardId,
  onBoardLoad,
  initialCardId,
  onInitialCardOpened,
  selectedWeek,
  boardBackground,
}: MyBoardProps = {}) {
  const { user } = useAuth();

  return (
    <KanbanBoard
      boardId={boardId}
      userId={!boardId ? user?.id : undefined}
      onBoardLoad={onBoardLoad}
      initialCardId={initialCardId}
      onInitialCardOpened={onInitialCardOpened}
      selectedWeek={selectedWeek}
      enableAutoCollapse={true}
      enableAlerts={true}
      boardBackground={boardBackground}
      screenMetadata={{
        title: 'Board',
        showHeader: false,
        showSidebar: false,
      }}
      emptyState={{
        title: 'No Active Board',
        description:
          'Create a new board from the board selector in the header, or ask your admin to set one up for you.',
        icon: <Icon name={IconName.KANBAN} size="xl" />,
      }}
    />
  );
}
